// R-162 §11.2 — daily-aggregate trend series for the FeedExhaustedPage session
// debrief sparklines. Delegates fetching to useOperatorTrend (the shared
// /api/analytics/operator-trend hook) and projects each daily row into three
// flat number[] series:
//
//   - avgTimeSeries  ← avgTimePerFieldMs (per-field timing; the only daily
//                       timing aggregate the endpoint exposes — there is no
//                       avg-time-per-DOC daily series)
//   - countSeries    ← docCount
//   - accuracySeries ← 1 - correctionRate (correctionRate IS daily, so an
//                       accuracy trend is available — the design's open
//                       question resolves YES)

import { useMemo } from "react";
import { useOperatorTrend } from "@/features/analytics/hooks/useOperatorTrend";

interface OperatorActivity {
  avgTimeSeries: number[];
  countSeries: number[];
  accuracySeries: number[];
  loading: boolean;
  error: Error | null;
}

export function useOperatorActivity(
  operatorId: string | undefined,
  days = 7,
): OperatorActivity {
  // Recompute the window only when `days` changes — stable refs keep
  // useOperatorTrend's fetch effect from re-firing every render.
  const { from, to } = useMemo(() => {
    const end = new Date();
    const start = new Date(end.getTime() - days * 86400_000);
    return { from: start.toISOString(), to: end.toISOString() };
  }, [days]);

  const { data, isLoading, error } = useOperatorTrend(
    operatorId,
    from,
    to,
    "day",
  );

  const rows = data?.data ?? [];
  return {
    avgTimeSeries: rows.map((r) => r.avgTimePerFieldMs),
    countSeries: rows.map((r) => r.docCount),
    // Round to 2dp so 1 - 0.05 doesn't render as 0.9500000000000001.
    accuracySeries: rows.map(
      (r) => Math.round((1 - r.correctionRate) * 100) / 100,
    ),
    loading: isLoading,
    error: error ? new Error(error) : null,
  };
}
